import React from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Divider,
  keyframes,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BuildIcon from "@mui/icons-material/Build";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { TOKENS, SectionTitle } from "./styled";
import type { Service } from "./types";
import ArticlePhotoStrip, { ArticlePhoto } from "./ArticlePhotoStrip";

export type SelectedArticle = {
  service: Service;
  quantity: number;
  comment?: string;
  photos?: ArticlePhoto[];
};

type Props = {
  services: Service[];
  selected: SelectedArticle[];
  onChange: (next: SelectedArticle[]) => void;
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const cardSlideIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

// ─── Modern search input (matches admin SearchInputWrapper) ───────────────────
const SearchField: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => (
  <TextField
    fullWidth
    placeholder="Rechercher une prestation..."
    value={value}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ color: TOKENS.GRAY_400 }} />
        </InputAdornment>
      ),
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        background: TOKENS.GRAY_100,
        transition: "all 0.2s",
        "& fieldset": { borderColor: "transparent" },
        "&:hover": {
          background: TOKENS.WHITE,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
        "&.Mui-focused": {
          background: TOKENS.WHITE,
          boxShadow: "0 4px 12px rgba(49,130,206,0.15)",
          "& fieldset": {
            borderColor: TOKENS.ACCENT_BLUE,
            borderWidth: 2,
          },
        },
      },
    }}
  />
);

// ─── Dropdown of search hits ─────────────────────────────────────────────────
const Dropdown: React.FC<{
  items: Service[];
  onPick: (service: Service) => void;
}> = ({ items, onPick }) => (
  <Box
    sx={{
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      right: 0,
      background: TOKENS.WHITE,
      borderRadius: "10px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
      maxHeight: 300,
      overflowY: "auto",
      zIndex: 100,
      animation: `${slideIn} 0.18s ease-out`,
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-track": {
        background: TOKENS.GRAY_100,
        borderRadius: 3,
      },
      "&::-webkit-scrollbar-thumb": {
        background: TOKENS.GRAY_300,
        borderRadius: 3,
        "&:hover": { background: TOKENS.GRAY_400 },
      },
    }}
  >
    {items.length === 0 ? (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: TOKENS.GRAY_500 }}>
          Aucun article ne correspond
        </Typography>
      </Box>
    ) : (
      items.map((service, i) => (
        <Box
          key={service.id}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(service);
          }}
          sx={{
            padding: "12px 16px",
            cursor: "pointer",
            transition: "all 0.15s",
            borderBottom:
              i === items.length - 1
                ? "none"
                : `1px solid ${TOKENS.GRAY_100}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "&:hover": {
              background: TOKENS.GRAY_100,
              "& .article-name": { color: TOKENS.ACCENT_BLUE },
            },
          }}
        >
          <Typography
            className="article-name"
            sx={{
              color: TOKENS.PRIMARY_BLUE,
              fontWeight: 500,
              fontSize: "0.95rem",
              transition: "color 0.15s",
            }}
          >
            {service.name}
          </Typography>
          <Typography
            sx={{
              color: TOKENS.GRAY_600,
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            {service.price.toFixed(2)} €
          </Typography>
        </Box>
      ))
    )}
  </Box>
);

// ─── TOP-N quick-pick chip ──────────────────────────────────────────────────
const ArticleChip: React.FC<{
  service: Service;
  selectedCount: number;
  onAdd: () => void;
}> = ({ service, selectedCount, onAdd }) => (
  <Box
    onClick={onAdd}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      padding: "6px 12px",
      background: TOKENS.WHITE,
      border: `1px solid ${TOKENS.GRAY_200}`,
      borderRadius: "20px",
      fontSize: "0.8rem",
      fontWeight: 500,
      color: TOKENS.PRIMARY_BLUE,
      cursor: "pointer",
      transition: "all 0.2s",
      userSelect: "none",
      "&:hover": {
        background: TOKENS.ACCENT_BLUE,
        color: TOKENS.WHITE,
        borderColor: TOKENS.ACCENT_BLUE,
        transform: "translateY(-1px)",
        boxShadow: "0 2px 8px rgba(49,130,206,0.25)",
        "& .qty-badge": {
          background: TOKENS.WHITE,
          color: TOKENS.ACCENT_BLUE,
        },
      },
    }}
  >
    <Box component="span">{service.name}</Box>
    {selectedCount > 0 && (
      <Box
        className="qty-badge"
        component="span"
        sx={{
          background: TOKENS.ACCENT_BLUE,
          color: TOKENS.WHITE,
          borderRadius: "50%",
          width: 18,
          height: 18,
          fontSize: 11,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s",
        }}
      >
        {selectedCount}
      </Box>
    )}
  </Box>
);

// ─── Selected article card (admin ArticleCardWrapper styling) ───────────────
const SelectedCard: React.FC<{
  service: Service;
  quantity: number;
  comment: string;
  photos: ArticlePhoto[];
  onUpdate: (q: number) => void;
  onCommentChange: (c: string) => void;
  onPhotosChange: (p: ArticlePhoto[]) => void;
}> = ({
  service,
  quantity,
  comment,
  photos,
  onUpdate,
  onCommentChange,
  onPhotosChange,
}) => {
  const [showComment, setShowComment] = React.useState(comment.length > 0);
  const commentRef = React.useRef<HTMLInputElement | null>(null);

  const toggleComment = () => {
    setShowComment((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => commentRef.current?.focus(), 0);
      } else if (comment.length > 0) {
        onCommentChange("");
      }
      return next;
    });
  };

  return (
  <Box
    sx={{
      background: TOKENS.WHITE,
      border: `1px solid ${TOKENS.GRAY_200}`,
      borderRadius: "10px",
      padding: "1rem",
      transition: "all 0.2s",
      animation: `${cardSlideIn} 0.25s ease-out`,
      "&:hover": {
        borderColor: TOKENS.GRAY_300,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        transform: { md: "translateY(-2px)" },
      },
    }}
  >
    {/* Header: name + remove */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 1.25,
      }}
    >
      <Typography
        sx={{
          color: TOKENS.PRIMARY_BLUE,
          fontWeight: 600,
          fontSize: "0.95rem",
          lineHeight: 1.4,
          flex: 1,
          mr: 0.5,
        }}
      >
        {service.name}
      </Typography>
      <IconButton
        size="small"
        onClick={() => onUpdate(0)}
        sx={{
          padding: "6px",
          color: TOKENS.GRAY_400,
          transition: "all 0.2s",
          "&:hover": {
            color: TOKENS.DANGER_RED,
            background: "rgba(229,62,62,0.1)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: "1.25rem" }} />
      </IconButton>
    </Box>

    {/* Meta */}
    <Typography
      sx={{
        color: TOKENS.GRAY_600,
        fontSize: "0.75rem",
        fontWeight: 500,
        mb: 1.25,
      }}
    >
      {service.price.toFixed(2)} €
    </Typography>

    {/* Comment (toggleable) */}
    {showComment && (
      <TextField
        multiline
        rows={2}
        fullWidth
        autoFocus
        inputRef={commentRef}
        placeholder="Ex : armoire à 2 portes, livrée en 3 cartons, pas encore ouverte."
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        inputProps={{ maxLength: 500 }}
        sx={{
          mb: 1.25,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            background: TOKENS.GRAY_100,
            fontSize: "0.85rem",
            "& fieldset": { borderColor: "transparent" },
            "&:hover fieldset": { borderColor: TOKENS.GRAY_300 },
            "&.Mui-focused": {
              background: TOKENS.WHITE,
              "& fieldset": {
                borderColor: TOKENS.ACCENT_BLUE,
                borderWidth: 2,
              },
            },
          },
        }}
      />
    )}

    {/* Photos + comment toggle */}
    <Box sx={{ mb: 1.25 }}>
      <ArticlePhotoStrip
        photos={photos}
        onChange={onPhotosChange}
        extraActions={
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={toggleComment}
            sx={{ textTransform: "none" }}
          >
            {showComment ? "Masquer la précision" : "Ajouter une précision"}
          </Button>
        }
      />
    </Box>

    {/* Quantity control */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: TOKENS.GRAY_600,
          fontWeight: 600,
          fontSize: "0.8rem",
        }}
      >
        Quantité
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: TOKENS.GRAY_100,
          borderRadius: "8px",
          padding: "2px",
        }}
      >
        <IconButton
          size="small"
          onClick={() => onUpdate(quantity - 1)}
          sx={{
            padding: "4px",
            color: TOKENS.LIGHT_BLUE,
            "&:hover": {
              color: TOKENS.ACCENT_BLUE,
              background: "rgba(49,130,206,0.1)",
            },
          }}
        >
          <RemoveIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        <Typography
          sx={{
            minWidth: 24,
            textAlign: "center",
            fontWeight: 700,
            color: TOKENS.PRIMARY_BLUE,
            fontSize: "0.9rem",
          }}
        >
          {quantity}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onUpdate(quantity + 1)}
          sx={{
            padding: "4px",
            color: TOKENS.LIGHT_BLUE,
            "&:hover": {
              color: TOKENS.ACCENT_BLUE,
              background: "rgba(49,130,206,0.1)",
            },
          }}
        >
          <AddIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>
    </Box>
  </Box>
  );
};

// ─── Reusable shortcut row (TOP 10, Services, …) ─────────────────────────────
const ShortcutRow: React.FC<{
  label: string;
  description?: string;
  icon: React.ReactNode;
  gradient: string;
  shadow: string;
  items: Service[];
  selected: SelectedArticle[];
  onAdd: (service: Service) => void;
}> = ({ label, description, icon, gradient, shadow, items, selected, onAdd }) => (
  <Box>
    <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: shadow,
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 14,
            color: TOKENS.GRAY_800,
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {description && (
          <Typography
            sx={{
              color: TOKENS.GRAY_600,
              fontSize: 12,
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
    <Box display="flex" flexWrap="wrap" gap={0.875}>
      {items.map((service) => {
        const sel = selected.find((s) => s.service.id === service.id);
        return (
          <ArticleChip
            key={service.id}
            service={service}
            selectedCount={sel?.quantity ?? 0}
            onAdd={() => onAdd(service)}
          />
        );
      })}
    </Box>
  </Box>
);

// ─── Main ───────────────────────────────────────────────────────────────────
const ArticlesSection: React.FC<Props> = ({ services, selected, onChange }) => {
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const filtered = React.useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return services
      .filter((s) => s.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, services]);

  const top10 = React.useMemo(
    () => [...services].sort((a, b) => b.price - a.price).slice(0, 10),
    [services],
  );

  const servicesShortcut = React.useMemo(
    () =>
      services
        .filter((s) => s.is_service_category)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [services],
  );

  const selectedCount = selected.reduce((n, s) => n + s.quantity, 0);

  const add = (service: Service) => {
    const existing = selected.find((s) => s.service.id === service.id);
    if (existing) {
      onChange(
        selected.map((s) =>
          s.service.id === service.id ? { ...s, quantity: s.quantity + 1 } : s,
        ),
      );
    } else {
      onChange([...selected, { service, quantity: 1 }]);
    }
    setQuery("");
    setOpen(false);
  };

  const update = (id: string, quantity: number) => {
    if (quantity <= 0) {
      onChange(selected.filter((s) => s.service.id !== id));
    } else {
      onChange(
        selected.map((s) =>
          s.service.id === id ? { ...s, quantity } : s,
        ),
      );
    }
  };

  const setComment = (id: string, comment: string) => {
    onChange(
      selected.map((s) =>
        s.service.id === id ? { ...s, comment } : s,
      ),
    );
  };

  const setPhotos = (id: string, photos: ArticlePhoto[]) => {
    onChange(
      selected.map((s) =>
        s.service.id === id ? { ...s, photos } : s,
      ),
    );
  };

  return (
    <Box>
      <SectionTitle component="h2">
        Que souhaitez-vous faire installer ?
      </SectionTitle>

      {/* Search + dropdown */}
      <Box sx={{ position: "relative" }}>
        <Box
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          <SearchField value={query} onChange={setQuery} />
        </Box>
        {open && query.length > 0 && (
          <Dropdown items={filtered} onPick={add} />
        )}
      </Box>

      {/* Shortcut rows (TOP 10 + Services) */}
      {(top10.length > 0 || servicesShortcut.length > 0) && (
        <Box
          sx={{
            mt: 2,
            p: { xs: 1, sm: 1.5 },
            background: TOKENS.GRAY_100,
            borderRadius: "10px",
            border: `1px dashed ${TOKENS.GRAY_300}`,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {top10.length > 0 && (
            <ShortcutRow
              label="Les prestations les plus demandées"
              description="Cliquez sur une prestation pour l’ajouter directement."
              icon={<TrendingUpIcon sx={{ color: TOKENS.WHITE, fontSize: 16 }} />}
              gradient={`linear-gradient(135deg, ${TOKENS.ACCENT_BLUE} 0%, ${TOKENS.ACCENT_PURPLE} 100%)`}
              shadow="0 2px 6px rgba(49,130,206,0.25)"
              items={top10}
              selected={selected}
              onAdd={add}
            />
          )}
          {servicesShortcut.length > 0 && (
            <ShortcutRow
              label="Prestations complémentaires"
              description="En plus du montage, nos techniciens peuvent intervenir pour d’autres besoins."
              icon={<BuildIcon sx={{ color: TOKENS.WHITE, fontSize: 16 }} />}
              gradient={`linear-gradient(135deg, ${TOKENS.SUCCESS_GREEN} 0%, #2f855a 100%)`}
              shadow="0 2px 6px rgba(72,187,120,0.3)"
              items={servicesShortcut}
              selected={selected}
              onAdd={add}
            />
          )}
        </Box>
      )}

      {/* Selected articles grid */}
      {selected.length > 0 && (
        <Box mt={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, ${TOKENS.GRAY_300} 0%, transparent 100%)`,
              }}
            />
            <Typography
              sx={{
                color: TOKENS.LIGHT_BLUE,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Votre sélection ({selectedCount} prestation
              {selectedCount > 1 ? "s" : ""})
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, transparent 0%, ${TOKENS.GRAY_300} 100%)`,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(280px, 1fr))",
                md: "repeat(auto-fill, minmax(320px, 1fr))",
              },
              gap: { xs: "0.5rem", sm: "0.75rem", md: "1rem" },
            }}
          >
            {selected.map(({ service, quantity, comment, photos }) => (
              <SelectedCard
                key={service.id}
                service={service}
                quantity={quantity}
                comment={comment ?? ""}
                photos={photos ?? []}
                onUpdate={(q) => update(service.id, q)}
                onCommentChange={(c) => setComment(service.id, c)}
                onPhotosChange={(p) => setPhotos(service.id, p)}
              />
            ))}
          </Box>
        </Box>
      )}

      {selected.length === 0 && (
        <Box
          sx={{
            mt: 3,
            py: 4,
            textAlign: "center",
            color: TOKENS.GRAY_500,
            border: `1px dashed ${TOKENS.GRAY_300}`,
            borderRadius: "10px",
            background: TOKENS.GRAY_100,
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Aucune prestation ajoutée pour l&apos;instant. Utilisez la barre de
            recherche ou cliquez sur une prestation dans la liste ci-dessus
            pour commencer.
          </Typography>
        </Box>
      )}

      <Divider sx={{ mt: 0 }} />
    </Box>
  );
};

export default ArticlesSection;
